import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, Edit2 } from 'lucide-react';

const LinkActionPopup = ({ isOpen, position, linkUrl, onEdit, onOpen, onClose }) => {
  const popupRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !position) return null;

  const content = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={popupRef}
          initial={{ opacity: 0, scale: 0.95, y: -5 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -5 }}
          transition={{ duration: 0.15 }}
          className="fixed z-[10001] bg-white dark:bg-primary-card rounded-lg shadow-xl border border-gray-200 dark:border-dark-100 py-1 min-w-[140px]"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            transform: 'translateX(-50%)',
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
              onClose();
            }}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-light-200 hover:bg-gray-100 dark:hover:bg-dark-200 transition-colors first:rounded-t-lg"
          >
            <ExternalLink className="w-4 h-4 text-primary-accent" />
            <span>Open Link</span>
          </button>
          <div className="h-px bg-gray-200 dark:bg-dark-100 mx-2" />
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
              onClose();
            }}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-light-200 hover:bg-gray-100 dark:hover:bg-dark-200 transition-colors last:rounded-b-lg"
          >
            <Edit2 className="w-4 h-4 text-primary-accent" />
            <span>Edit Link</span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
};

export default LinkActionPopup;
